import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cancelTemplateApi, listTemplates } from '../lib/api';
import { translateError, useT } from '../lib/i18n';
import { BackButton } from '../lib/BackButton';
import { Card, CardHint, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import {
  maskHas,
  WEEKDAYS_MON_FIRST,
  WEEKDAY_SHORT_KEYS,
  type WeekdayIndex,
} from '@waylo/shared';
import { hapticSuccess, hapticError, hapticWarning, showAlert, showConfirm } from '../lib/telegram';
import { useState } from 'react';

export function MyTemplatesPage(): JSX.Element {
  const { t, locale } = useT();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({ queryKey: ['templates'], queryFn: listTemplates });
  const cancel = useMutation({
    mutationFn: (id: string) => cancelTemplateApi(id, true),
    onMutate: async (id) => {
      // Optimistic: flip the template inactive so its cancel button + active
      // badge update at once. The cancelled-rides count alert still waits for
      // the server response in onSuccess.
      await qc.cancelQueries({ queryKey: ['templates'] });
      const prev = qc.getQueryData<typeof q.data>(['templates']);
      if (prev) {
        qc.setQueryData(['templates'], {
          ...prev,
          items: prev.items.map((tpl) => (tpl.id === id ? { ...tpl, isActive: false } : tpl)),
        });
      }
      return { prev };
    },
    onSuccess: async (r) => {
      hapticSuccess();
      await showAlert(t('my.templates.cancelled', { n: r.cancelledRides }));
    },
    onError: (e: Error, _id, ctx) => {
      hapticError();
      if (ctx?.prev) qc.setQueryData(['templates'], ctx.prev);
      setError(translateError(t, e));
    },
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: ['templates'] });
      await qc.invalidateQueries({ queryKey: ['my-rides'] });
    },
  });

  const nameKey: 'nameSr' | 'nameEn' = locale === 'en' ? 'nameEn' : 'nameSr';

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col px-4 py-6">
      <BackButton />
      <h1 className="mb-4 text-2xl font-bold text-tg-text">{t('my.templates.title')}</h1>

      {error && (
        <p className="mb-3 rounded-card bg-destructive/10 p-3 text-sm text-destructive">{error}</p>
      )}

      {q.isLoading && <div className="h-32 animate-pulse rounded-card bg-tg-bg-secondary" />}
      {q.data && q.data.items.length === 0 && (
        <Card className="text-center">
          <CardTitle>{t('my.templates.empty.t')}</CardTitle>
          <CardHint>{t('my.templates.empty.b')}</CardHint>
        </Card>
      )}

      <div className="space-y-3">
        {q.data?.items.map((tpl) => {
          const daysHuman = WEEKDAYS_MON_FIRST.filter((d) => maskHas(tpl.weekdayMask, d as WeekdayIndex))
            .map((d) => t(`weekday.${WEEKDAY_SHORT_KEYS[d]}` as 'weekday.mon'))
            .join(', ');
          return (
            <Card key={tpl.id} className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <p className="font-semibold text-tg-text">
                  {tpl.route.origin[nameKey]} → {tpl.route.destination[nameKey]}
                </p>
                {tpl.isActive ? (
                  <Badge tone="success">●</Badge>
                ) : (
                  <Badge tone="neutral">○</Badge>
                )}
              </div>
              <CardHint>
                {t('my.templates.everyWeek', { days: daysHuman, time: tpl.departureTime })}
              </CardHint>
              <p className="text-xs text-tg-hint">
                {Math.round(tpl.proposedCost).toLocaleString()} {tpl.currency} /{' '}
                {t('common.perSeat')} · {tpl.seatsTotal} {t('common.seat_other')}
              </p>
              <p className="text-xs text-tg-hint">
                {t('my.templates.upcoming', { n: tpl.upcomingCount })}
              </p>
              {tpl.isActive && (
                <button
                  type="button"
                  onClick={async () => {
                    hapticWarning();
                    const ok = await showConfirm(t('my.templates.cancelConfirm'));
                    if (ok) cancel.mutate(tpl.id);
                  }}
                  disabled={cancel.isPending}
                  className="mt-1 text-xs font-medium text-destructive underline disabled:opacity-50"
                >
                  {t('my.templates.cancel')}
                </button>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
